import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CollectabilityGauge } from '@/components/search/CollectabilityGauge';
import { ValidationScoreCard } from '@/components/search/ValidationScoreCard';
import { ValidationBadge } from '@/components/search/ValidationBadge';
import { RecoveryMatrix } from '@/components/search/RecoveryMatrix';
import { EnrichedDataDisplay } from '@/components/search/EnrichedDataDisplay';
import { singleSearchAPI } from '@/services/singleSearchAPI';
import type { SingleSearchResult } from '@/types';
import { formatCurrency, formatDate } from '@/utils/searchValidation';
import { logger } from '@/utils/logger';
// Icons removed for cleaner design

export const SearchResultsPage: React.FC = () => {
  const { searchId } = useParams<{ searchId: string }>();
  const navigate = useNavigate();

  const [searchResult, setSearchResult] = useState<SingleSearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!searchId) {
      logger.error('No search ID provided in results page');
      navigate('/search');
      return;
    }

    const loadResults = async () => {
      try {
        logger.info('Loading search results', { searchId });
        const result = await singleSearchAPI.getSearchResults(searchId);
        setSearchResult(result);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load search results';
        logger.error('Failed to load search results:', err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadResults();
  }, [searchId, navigate]);

  const handleDownloadPDF = () => {
    logger.info('PDF download requested', { searchId });
    // TODO: Implement PDF export
    alert('PDF export functionality coming soon!');
  };

  const handleShare = () => {
    logger.info('Share requested', { searchId });
    // TODO: Implement share functionality
    alert('Share functionality coming soon!');
  };

  const scrollToEnrichedData = () => {
    const element = document.getElementById('enriched-data');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-collectability-blue border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (error || !searchResult) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">❌</span>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Error Loading Results</h2>
            <p className="text-sm text-slate-600 mb-4">{error || 'Results not found'}</p>
            <Button onClick={() => navigate('/search')} className="text-sm">Back to Search</Button>
          </Card>
        </div>
      </div>
    );
  }

  const {
    searchRequest,
    primaryVerified,
    collectabilityScore,
    collectabilityBand,
    insights,
    recoveryScenarios,
    aiOverview,
    aiRiskAssessment,
    aiRecommendedStrategy,
    personalInfo,
    financialProfile,
    timestamp
  } = searchResult;

  // Determine which operation type to determine what to display
  const searchType = searchRequest.searchType;
  const isScoringOperation = searchType === 'scoring' || searchType === 'scoring_enrich';
  const isValidationOperation = searchType === 'validate' || searchType === 'enrich_validate';
  const isEnrichmentOperation = searchType === 'enrich' || searchType.includes('enrich');

  // Validate critical data
  if (!searchRequest) {
    logger.error('Search result missing searchRequest data', { searchResult });
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">❌</span>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Invalid Search Data</h2>
            <p className="text-sm text-slate-600 mb-4">The search data is incomplete or corrupted. Please try running a new search.</p>
            <Button onClick={() => navigate('/search')} className="text-sm">Back to Search</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/search')}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            New Search
          </button>

          <div className="flex items-center gap-2">
            {personalInfo && (
              <Button variant="outline" onClick={scrollToEnrichedData} className="hidden sm:flex text-sm">
                Jump to Contact Info
              </Button>
            )}
            <Button variant="outline" onClick={handleShare} className="hidden sm:flex text-sm">
              Share
            </Button>
            <Button onClick={handleDownloadPDF} className="text-sm">
              Download PDF
            </Button>
          </div>
        </div>

        {/* Page Title */}
        <h1 className="text-2xl font-bold text-slate-900 mb-4">CollectABILITY Report</h1>

        {/* Top Row: Searched Information + First/Last Seen */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Searched Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="p-3 bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200 h-full flex flex-col">
              <h3 className="text-xs font-semibold text-slate-900 mb-2 uppercase tracking-wide">Searched Information</h3>
              <h2 className="text-lg font-bold text-slate-900 mb-2">
                {searchRequest.firstName} {searchRequest.lastName}
              </h2>
              <div className="text-xs text-slate-600 space-y-1 flex-1">
                {/* Address - handle both single and multiple entries */}
                {searchRequest.addresses && searchRequest.addresses.length > 0 ? (
                  <div>
                    {searchRequest.addresses.map((addr, idx) => (
                      addr.address && (
                        <p key={idx} className="mb-1">
                          {addr.address}
                          {addr.city && `, ${addr.city}`}
                          {addr.state && `, ${addr.state}`}
                          {addr.zip && ` ${addr.zip}`}
                        </p>
                      )
                    ))}
                  </div>
                ) : searchRequest.address ? (
                  <p>
                    {searchRequest.address}
                    {searchRequest.city && `, ${searchRequest.city}`}
                    {searchRequest.state && `, ${searchRequest.state}`}
                    {searchRequest.zip && ` ${searchRequest.zip}`}
                  </p>
                ) : (
                  <p className="text-slate-400 italic">Address not provided</p>
                )}

                {/* Phone - handle both single and multiple entries */}
                {searchRequest.phones && searchRequest.phones.length > 0 ? (
                  <div>
                    {searchRequest.phones.filter(p => p && p.trim().length > 0).map((phone, idx) => (
                      <p key={idx}>Phone: {phone}</p>
                    ))}
                    {searchRequest.phones.filter(p => p && p.trim().length > 0).length === 0 && (
                      <p>Phone: <span className="text-slate-400 italic">Not provided</span></p>
                    )}
                  </div>
                ) : (
                  <p>Phone: {searchRequest.phone || <span className="text-slate-400 italic">Not provided</span>}</p>
                )}

                {/* Email - handle both single and multiple entries */}
                {searchRequest.emails && searchRequest.emails.length > 0 ? (
                  <div>
                    {searchRequest.emails.filter(e => e && e.trim().length > 0).map((email, idx) => (
                      <p key={idx}>Email: {email}</p>
                    ))}
                    {searchRequest.emails.filter(e => e && e.trim().length > 0).length === 0 && (
                      <p>Email: <span className="text-slate-400 italic">Not provided</span></p>
                    )}
                  </div>
                ) : (
                  <p>Email: {searchRequest.email || <span className="text-slate-400 italic">Not provided</span>}</p>
                )}
              </div>
              {isScoringOperation && searchRequest.amountToCollect && (
                <div className="mt-auto pt-2 border-t border-slate-200">
                  <p className="text-[10px] text-slate-600 mb-0.5 uppercase tracking-wide">Amount to Collect</p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(searchRequest.amountToCollect)}
                  </p>
                </div>
              )}
            </Card>
          </motion.div>

          {/* Activity Timeline */}
          {primaryVerified && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <Card className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 h-full flex flex-col">
                <h3 className="text-xs font-semibold text-slate-900 mb-2 uppercase tracking-wide">Activity Timeline</h3>
                <div className="space-y-2 flex-1">
                  <div className="bg-white/80 rounded p-2 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-[10px] text-slate-600 uppercase tracking-wide">First Seen</span>
                      </div>
                      <span className="font-semibold text-slate-900 text-xs">{formatDate(primaryVerified.firstSeen)}</span>
                    </div>
                  </div>
                  <div className="bg-white/80 rounded p-2 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-[10px] text-slate-600 uppercase tracking-wide">Last Seen</span>
                      </div>
                      <span className="font-semibold text-slate-900 text-xs">{formatDate(primaryVerified.lastSeen)}</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded p-2 border border-blue-300">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-blue-700 uppercase tracking-wide font-medium">Duration</span>
                      <span className="font-bold text-blue-900 text-xs">
                        {(() => {
                          const firstSeen = new Date(primaryVerified.firstSeen);
                          const lastSeen = new Date(primaryVerified.lastSeen);
                          const years = lastSeen.getFullYear() - firstSeen.getFullYear();
                          return `${years} year${years !== 1 ? 's' : ''}`;
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-2 pt-2 border-t border-blue-200">
                  Search ID: {searchResult.id?.substring(0, 20)}...
                </div>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Compact Score Cards Row for Validation Operations */}
        {isValidationOperation && primaryVerified && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <ValidationScoreCard type="identity" score={primaryVerified.identityConfidenceScore || 0} />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <ValidationScoreCard type="fraud" score={primaryVerified.fraudRisk || 0} />
            </motion.div>
          </div>
        )}

        {/* Main Content Row: Score Gauge (left) + Verification + IntelligentDebt (right stacked) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Score Gauge for Scoring Operations - LEFT SIDE (1/3 width) */}
          {isScoringOperation && collectabilityScore !== undefined && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-1 flex flex-col"
            >
              <Card className="p-4 h-full flex flex-col items-center justify-center">
                <CollectabilityGauge score={collectabilityScore} />
                <p className="text-xs text-slate-600 mt-3 text-center font-medium">{collectabilityBand}</p>
              </Card>
            </motion.div>
          )}

          {/* RIGHT SIDE (2/3 width) - Stacked: Verification + IntelligentDebt */}
          <div className={isScoringOperation ? "lg:col-span-2 flex flex-col gap-4" : "lg:col-span-3"}>
            {/* Primary Validated Information */}
            {primaryVerified && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: isValidationOperation ? 0.2 : 0.1 }}
                className="flex-1"
              >
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 h-full flex flex-col">
                  <h3 className="text-base font-semibold text-slate-900 mb-6 uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Primary Verified Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 flex-1">
                    {/* Left Column: Name + DOB + Address */}
                    <div className="space-y-5">
                      <div>
                        <span className="text-xs text-slate-600 block mb-2 uppercase tracking-wide font-medium">Name</span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-slate-900 text-lg">{primaryVerified.name}</span>
                        </div>
                      </div>
                      {primaryVerified.age && (
                        <div>
                          <span className="text-xs text-slate-600 block mb-2 uppercase tracking-wide font-medium">Age / DOB</span>
                          <span className="font-semibold text-slate-900 text-base block">
                            {primaryVerified.age} years
                            {primaryVerified.dateOfBirth && ` (${primaryVerified.dateOfBirth})`}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-xs text-slate-600 block mb-2 uppercase tracking-wide font-medium">Address</span>
                        <div className="flex items-start gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-base flex-1 leading-relaxed min-w-[200px]">{primaryVerified.address}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Phone + Email */}
                    <div className="space-y-5">
                      <div>
                        <span className="text-xs text-slate-600 block mb-2 uppercase tracking-wide font-medium">Phone</span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-lg">{primaryVerified.phone || 'N/A'}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-600 block mb-2 uppercase tracking-wide font-medium">Email</span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-base break-all">{primaryVerified.email || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* IntelligentDebt Insights - Stacked below verification in right column */}
            {isScoringOperation && insights && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                <Card className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">
                    IntelligentDebt Insights
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <span className="text-[10px] text-slate-600 block mb-1 uppercase tracking-wide font-medium">Income 360</span>
                      <span className="font-bold text-slate-900 text-base">${insights.income360.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-600 block mb-1 uppercase tracking-wide font-medium">Affluence Index</span>
                      <span className="font-bold text-slate-900 text-base">{insights.affluenceIndex}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-600 block mb-1 uppercase tracking-wide font-medium">Spending Power</span>
                      <span className="font-bold text-slate-900 text-base">${insights.spendingPower.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-600 block mb-1 uppercase tracking-wide font-medium">Financial Durability</span>
                      <span className="font-bold text-slate-900 text-base">{insights.financialDurabilityIndex}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-600 block mb-1 uppercase tracking-wide font-medium">Economic Cohort</span>
                      <span className="font-bold text-slate-900 text-sm">{insights.economicCohort}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}
          </div>
        </div>

        {/* 3. Financial Profile Card (only for scoring_enrich) */}
        {isScoringOperation && financialProfile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mb-4 mt-auto"
          >
            <Card className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 h-full">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">Financial Profile</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-xs text-slate-600 block mb-0.5">Estimated Net Worth</span>
                  <span className="font-bold text-slate-900 text-lg">{financialProfile.estimatedNetWorth}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-600 block mb-0.5">Bankruptcy</span>
                  <span className="font-semibold text-slate-900 text-base">
                    {financialProfile.hasBankruptcy ? `Yes (${financialProfile.bankruptcyCount})` : 'No'}
                  </span>
                  {financialProfile.mostRecentBankruptcy && (
                    <p className="text-xs text-slate-600 mt-0.5">Most recent: {formatDate(financialProfile.mostRecentBankruptcy)}</p>
                  )}
                </div>
                <div>
                  <span className="text-xs text-slate-600 block mb-0.5">Judgments</span>
                  <span className="font-semibold text-slate-900 text-base">{financialProfile.judgmentCount}</span>
                  {financialProfile.mostRecentJudgment && (
                    <p className="text-xs text-slate-600 mt-0.5">Most recent: {formatDate(financialProfile.mostRecentJudgment)}</p>
                  )}
                </div>
                <div>
                  <span className="text-xs text-slate-600 block mb-0.5">Liens</span>
                  <span className="font-semibold text-slate-900 text-base">{financialProfile.lienCount}</span>
                  {financialProfile.mostRecentLien && (
                    <p className="text-xs text-slate-600 mt-0.5">Most recent: {formatDate(financialProfile.mostRecentLien)}</p>
                  )}
                </div>
                {financialProfile.propertyValues.length > 0 && (
                  <div className="md:col-span-2">
                    <span className="text-xs text-slate-600 block mb-1">Property Values</span>
                    <div className="space-y-1">
                      {financialProfile.propertyValues.map((property, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-semibold text-slate-900">{formatCurrency(property.value)}</span>
                          <span className="text-slate-600"> - {property.address}</span>
                          <span className="text-xs text-slate-500"> (Sold: {formatDate(property.lastSold)})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        )}

        {/* 5. Recovery Matrix - Full Width (scoring operations only) */}
        {isScoringOperation && recoveryScenarios && searchRequest.amountToCollect && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-6"
          >
            <RecoveryMatrix
              scenarios={recoveryScenarios}
              amountToCollect={searchRequest.amountToCollect}
            />
          </motion.div>
        )}

        {/* 6. AI Analysis - Full Width (Phase 2: Will be populated via Bedrock) */}
        {aiOverview && aiRiskAssessment && aiRecommendedStrategy && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mb-6"
          >
            <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200">
              <h2 className="text-base font-semibold text-slate-900 mb-3">AI Analysis</h2>

              <div className="space-y-3">
                <div>
                  <h3 className="text-xs font-semibold text-slate-900 mb-1.5 uppercase tracking-wide">Overview</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">{aiOverview}</p>
                </div>

                <div className="border-t border-amber-200 pt-3">
                  <h3 className="text-xs font-semibold text-slate-900 mb-1.5 uppercase tracking-wide">Risk Assessment</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">{aiRiskAssessment}</p>
                </div>

                <div className="border-t border-amber-200 pt-3">
                  <h3 className="text-xs font-semibold text-slate-900 mb-1.5 uppercase tracking-wide">Recommended Strategy</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">{aiRecommendedStrategy}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* 7. Enriched Data / Skip Trace - Full Width (enrichment operations only) */}
        {isEnrichmentOperation && personalInfo ? (
          <motion.div
            id="enriched-data"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-6 scroll-mt-24"
          >
            <div className="mb-3">
              <h2 className="text-lg font-semibold text-slate-900">
                Contact & Identity Information
              </h2>
              <p className="text-xs text-slate-600 mt-1">Skip trace data and verified contact details</p>
            </div>
            <EnrichedDataDisplay personalInfo={personalInfo} />
          </motion.div>
        ) : isEnrichmentOperation && !personalInfo && isScoringOperation ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-6"
          >
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">Enrichment Data Not Available</h3>
                  <p className="text-sm text-slate-700 mb-2">
                    No enrichment data available for this individual. However, we were able to successfully complete the collectability scoring based on the Kount validation data.
                  </p>
                  <div className="bg-white/60 rounded-lg p-3 border border-blue-200 mt-3">
                    <p className="text-xs text-slate-600 mb-1 font-medium">Billing Information</p>
                    <p className="text-xs text-slate-700">
                      You were charged for <span className="font-semibold">CollectABILITY Scoring Only</span> since enrichment data was not available. The scoring report above includes your collectability score, recovery recommendations, and AI insights based on the validated identity information.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        ) : isEnrichmentOperation && !personalInfo ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-6"
          >
            <Card className="p-4 text-center bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Enriched Data Not Available</h3>
              <p className="text-sm text-slate-600 mb-3">
                The enrichment data could not be retrieved for this search. Please try again or contact support if the issue persists.
              </p>
            </Card>
          </motion.div>
        ) : null}

        {/* Footer Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-slate-100 rounded-lg"
        >
          <div>
            <p className="text-sm font-medium text-slate-900">Need another search?</p>
          </div>
          <Button onClick={() => navigate('/search')} className="text-sm">
            Run New Search
          </Button>
        </motion.div>
      </main>
    </div>
  );
};
